import {
    DEMO_DOCTOR, DEMO_PRAXIS, DEMO_TOKEN,
    DEMO_RECIPIENTS, DEMO_PATIENTS, DEMO_INVOICES,
    DEMO_EXAMS, DEMO_DASHBOARD_ACTIVITY, DEMO_OPEN_INVOICES,
    DEMO_QUOTA, DEMO_SETTINGS, DEMO_QUESTION,
} from "./mockData";

export const DEMO_KEY = "medinvoice_demo";
const DEMO_DOCTOR_KEY = "medinvoice_demo_doctor";
const DEMO_PRAXIS_KEY = "medinvoice_demo_praxis";
const BACKUP_TOKEN_KEY = "medinvoice_demo_backup_token";
const BACKUP_DOCTOR_KEY = "medinvoice_demo_backup_doctor";
const BACKUP_PRAXIS_KEY = "medinvoice_demo_backup_praxis";
const BANKSAPI_PENDING_KEY = "banksapi_pending";

export function isDemoMode(): boolean {
    // Demo should be session-scoped (closing the tab should end demo)
    return sessionStorage.getItem(DEMO_KEY) === "true";
}

export function activateDemo(overrides?: { doctor?: unknown; praxis?: unknown }): void {
    // Backup any existing real session once.
    if (!isDemoMode()) {
        const token = localStorage.getItem("token");
        const doctor = localStorage.getItem("doctor");
        const praxis = localStorage.getItem("praxis");
        if (token)  localStorage.setItem(BACKUP_TOKEN_KEY, token);
        if (doctor) localStorage.setItem(BACKUP_DOCTOR_KEY, doctor);
        if (praxis) localStorage.setItem(BACKUP_PRAXIS_KEY, praxis);
    }

    sessionStorage.setItem(DEMO_KEY, "true");

    localStorage.setItem("token", DEMO_TOKEN);
    localStorage.setItem("doctor", JSON.stringify((overrides?.doctor as any) ?? DEMO_DOCTOR));
    localStorage.setItem("praxis", JSON.stringify((overrides?.praxis as any) ?? DEMO_PRAXIS));
    if (overrides?.doctor) localStorage.setItem(DEMO_DOCTOR_KEY, JSON.stringify(overrides.doctor));
    if (overrides?.praxis) localStorage.setItem(DEMO_PRAXIS_KEY, JSON.stringify(overrides.praxis));
}

export function deactivateDemo(): void {
    sessionStorage.removeItem(DEMO_KEY);

    // Clear all demo-specific persisted state
    localStorage.removeItem(BANKSAPI_PENDING_KEY);

    // Restore previous real session if present; otherwise clear.
    const token = localStorage.getItem(BACKUP_TOKEN_KEY);
    const doctor = localStorage.getItem(BACKUP_DOCTOR_KEY);
    const praxis = localStorage.getItem(BACKUP_PRAXIS_KEY);
    if (token && doctor && praxis) {
        localStorage.setItem("token", token);
        localStorage.setItem("doctor", doctor);
        localStorage.setItem("praxis", praxis);
    } else {
        localStorage.removeItem("token");
        localStorage.removeItem("doctor");
        localStorage.removeItem("praxis");
    }

    localStorage.removeItem(BACKUP_TOKEN_KEY);
    localStorage.removeItem(BACKUP_DOCTOR_KEY);
    localStorage.removeItem(BACKUP_PRAXIS_KEY);
    localStorage.removeItem(DEMO_DOCTOR_KEY);
    localStorage.removeItem(DEMO_PRAXIS_KEY);
}

function readJsonSafe(key: string): any | null {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

function getDemoDoctor(): any {
    return readJsonSafe(DEMO_DOCTOR_KEY) ?? DEMO_DOCTOR;
}

function getDemoPraxis(): any {
    return readJsonSafe(DEMO_PRAXIS_KEY) ?? DEMO_PRAXIS;
}

// Returns mock data for a given API URL, or null if no match (fall through to real API).
export function getDemoResponse(url: string, method: string): unknown | null {
    const path = url.replace(/^\/api/, "").split("?")[0];
    const query = url.includes("?") ? new URLSearchParams(url.split("?")[1]) : new URLSearchParams();

    // Non-GET requests in demo mode: silently succeed
    if (method !== "GET") {
        return {};
    }

    // ── Recipients ────────────────────────────────────────────────────────────
    if (path === "/recipients") {
        return DEMO_RECIPIENTS;
    }
    const recipientMatch = path.match(/^\/recipients\/([^/]+)$/);
    if (recipientMatch) {
        return DEMO_RECIPIENTS.find(r => r.id === recipientMatch[1]) ?? DEMO_RECIPIENTS[0];
    }
    if (path.match(/^\/recipients\/[^/]+\/questionnaire$/)) {
        return { submission: null, questions: [] };
    }

    // ── Patients ──────────────────────────────────────────────────────────────
    if (path === "/patients") {
        return { patients: DEMO_PATIENTS };
    }
    const patientMatch = path.match(/^\/patients\/([^/]+)$/);
    if (patientMatch) {
        return DEMO_PATIENTS.find(p => p.id === patientMatch[1]) ?? DEMO_PATIENTS[0];
    }

    // ── Invoices ──────────────────────────────────────────────────────────────
    if (path === "/invoices") {
        return { invoices: DEMO_INVOICES, total: DEMO_INVOICES.length, filteredTotal: DEMO_INVOICES.length };
    }
    if (path === "/invoices/open") {
        return DEMO_OPEN_INVOICES;
    }
    const invoiceMatch = path.match(/^\/invoices\/([^/]+)$/);
    if (invoiceMatch) {
        return DEMO_INVOICES.find(i => i.id === invoiceMatch[1]) ?? DEMO_INVOICES[0];
    }
    if (path.match(/^\/invoices\/[^/]+\/questions$/)) {
        const invId = path.split("/")[2];
        if (invId === "demo-inv-8") return [DEMO_QUESTION];
        return [];
    }

    // ── Exams ─────────────────────────────────────────────────────────────────
    if (path === "/exams") {
        const invoiceId = query.get("invoiceId") ?? "";
        return DEMO_EXAMS[invoiceId] ?? [];
    }

    // ── Dashboard ─────────────────────────────────────────────────────────────
    if (path === "/dashboard/activity") {
        return DEMO_DASHBOARD_ACTIVITY;
    }
    if (path === "/dashboard/questions-count") {
        return {
            count: 1,
            items: [{
                id:                 DEMO_QUESTION.id,
                recipientId:        DEMO_QUESTION.recipientId,
                recipientName:      DEMO_QUESTION.recipientName,
                category:           "amount",
                invoiceId:          DEMO_QUESTION.invoiceId,
                invoiceReadableId:  DEMO_QUESTION.readableId,
            }],
        };
    }

    // ── Charges / quota ───────────────────────────────────────────────────────
    if (path.startsWith("/charges")) {
        const now = new Date();
        return {
            period: { year: now.getFullYear(), month: now.getMonth() + 1 },
            paid: false,
            paidAt: null,
            planSummary: {
                plan: 3,
                baseFee: 149,
                includedDoctors: 2,
                doctorCount: 1,
                extraDoctors: 0,
                extraDoctorFee: 0,
                includedStaff: 2,
                staffCount: 0,
                extraStaff: 0,
                extraStaffFee: 0,
                invoiceAllowancePerDoctor: null,
                totalAllowance: null,
                smsBilled: false,
            },
            invoices: { count: 8, paidCount: 4, amount: 2608.50, overageFee: 0, byDoctor: [] },
            sms:     { count: 0, rate: 0.09, total: 0 },
            letters: { count: 0, surcharge: 0, lxTotal: 0, total: 0 },
            grandTotal: 149,
            invoiceRows: [],
            smsRows: [],
            letterRows: [],
        };
    }
    if (path === "/sepa/billing") {
        return null;
    }

    // ── Doctors (team list) ───────────────────────────────────────────────────
    if (path === "/doctors") {
        return [getDemoDoctor()];
    }

    // ── Praxis ────────────────────────────────────────────────────────────────
    if (path === "/praxis") {
        return getDemoPraxis();
    }

    // ── Settings ──────────────────────────────────────────────────────────────
    if (path.startsWith("/settings")) {
        return DEMO_SETTINGS;
    }

    // ── Upload history ────────────────────────────────────────────────────────
    if (path === "/uploads") {
        return { invoices: [] };
    }

    // ── Notifications / LetterXpress ──────────────────────────────────────────
    if (path.startsWith("/notifications")) {
        return { pages: 1, price: 0.89 };
    }

    // ── Patient portal ────────────────────────────────────────────────────────
    if (path.startsWith("/patient-portal")) {
        return { link: "#" };
    }

    // Unknown endpoint — return empty object so the app doesn't crash
    return {};
}
